import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  ChartLine, Plus, ListChecks, Users, Bank, Calculator,
  Receipt, UsersThree, GearSix, SignOut,
  List, TrendDown, Wallet, ChartDonut, CurrencyCircleDollar, ShieldCheck, Robot, Money,
} from '@phosphor-icons/react';
import Logo from './Logo';

const navAll = [
  { to: '/', label: '仪表盘', icon: ChartLine, role: 'all' },
  { to: '/new-order', label: '录订单', icon: Plus, role: 'all' },
  { to: '/orders', label: '订单列表', icon: ListChecks, role: 'all' },
  { to: '/customers', label: '客户管理', icon: Users, role: 'all' },
  { to: '/my-salary', label: '我的工资', icon: Money, role: 'employee_or_manager' },
  { to: '/audit', label: '删除日志', icon: ShieldCheck, role: 'manager_or_admin' },
  { to: '/banks', label: '银行账户', icon: Bank, role: 'admin' },
  { to: '/reconcile', label: '银行结算', icon: Calculator, role: 'admin' },
  { to: '/expenses', label: '开销管理', icon: Receipt, role: 'admin' },
  { to: '/employees', label: '员工管理', icon: UsersThree, role: 'admin' },
  { to: '/advances', label: '借支与工资', icon: Wallet, role: 'admin' },
  { to: '/report', label: '月度报表', icon: ChartDonut, role: 'admin' },
  { to: '/rate', label: '汇率设置', icon: CurrencyCircleDollar, role: 'admin' },
  { to: '/telegram', label: 'Telegram 通知', icon: Robot, role: 'admin' },
  { to: '/settings', label: '系统设置', icon: GearSix, role: 'all' },
];

function NavItem({ to, label, icon: Icon, onClick }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={`nav-${to.replace('/', '') || 'dashboard'}`}
      className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg mx-2 transition-all ${
        isActive
          ? 'text-white bg-indigo-600/15 border border-indigo-500/30'
          : 'text-slate-400 hover:text-white hover:bg-slate-800/70'
      }`}
    >
      <Icon size={18} weight={isActive ? 'fill' : 'duotone'} />
      <span>{label}</span>
    </Link>
  );
}

export default function Layout({ children }) {
  const { user, logout, rateNotice } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = navAll.filter((i) => {
    if (i.role === 'all') return true;
    if (i.role === 'admin') return user?.role === 'admin';
    if (i.role === 'manager_or_admin') return user?.role === 'admin' || user?.role === 'manager';
    if (i.role === 'employee_or_manager') return user?.role === 'employee' || user?.role === 'manager';
    return false;
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const SidebarContent = (
    <>
      <div className="px-5 py-5 border-b border-slate-800/70">
        <Logo size="md" showText />
      </div>

      {/* User */}
      <div className="px-4 py-4 border-b border-slate-800/70">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
            {user?.name?.[0] || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate">
              {user?.role === 'admin' ? '管理员' : user?.role === 'manager' ? '经理' : '员工'} · {user?.email}
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 space-y-0.5">
        {items.map((it) => (
          <NavItem key={it.to} {...it} onClick={() => setMobileOpen(false)} />
        ))}
      </nav>

      <div className="p-3 border-t border-slate-800/70">
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
        >
          <SignOut size={18} weight="duotone" />
          <span>登出</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg-base)' }}>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col fixed left-0 top-0 h-screen bg-[#0B1121]/95 backdrop-blur-xl border-r border-slate-800">
        {SidebarContent}
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 h-full bg-[#0B1121] border-r border-slate-800 flex flex-col">
            {SidebarContent}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-[#0B1121]/95 backdrop-blur-xl border-b border-slate-800 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} className="text-slate-300" data-testid="mobile-menu-btn">
            <List size={24} />
          </button>
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="text-white font-semibold text-sm">MB 968</span>
          </div>
          <div className="w-8" />
        </header>

        {/* Rate notice for employees */}
        {rateNotice && (
          <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2.5 flex items-center justify-center gap-2 text-sm">
            <TrendDown size={16} weight="bold" className="text-amber-400" />
            <span className="text-amber-200">
              ⚡ 汇率更新:<span className="font-num font-semibold mx-1">{rateNotice.from}</span>
              →
              <span className="font-num font-semibold mx-1 text-amber-100">{rateNotice.to}</span>
            </span>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
